/** Accepting a figure that has no document — **ADR-0067, decision 2A** (card C-E, finding F-003).
 *
 * `POST /inventory/activities/{id}/verify` took no screen: a manual figure entered on a person's
 * attestation entered the totals and could never reach `VERIFIED`, which is the only state a figure
 * may be filed from. This is the client for it. It sends **no body**: the verifier is the signed-in
 * person, never a field, and a request that could name one would be asserting an authorization
 * answer.
 *
 * **The refusal keeps its code** (`refusalFrom`, not `new Error(message)`). The route has six
 * refusals in a fixed order and they are three different kinds of thing — *about the figure*
 * (`has_a_document`, `no_stated_basis`, `superseded`, `already_verified`), *about the person*
 * (`attester_cannot_verify`) and *about the record's state of play* (`blocking_flag_open`) — so a
 * client holding only a sentence cannot say which remedy applies.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { Refused, refusalFrom } from "./refusal";
import type { components } from "./schema";

export type VerifiedActivity = components["schemas"]["VerifiedActivityOut"];

/** The refusals the route can give for a *figure's* state, in the server's order
 * (`inventory/verification.py::refusal_for`), plus the 404 for absence. The two that are not about
 * the figure are `attester_cannot_verify` and `blocking_flag_open`; the role refusal is
 * `insufficient_role` and is not this module's. */
export const VERIFY_REFUSAL_CODES = [
  "has_a_document",
  "no_stated_basis",
  "superseded",
  "already_verified",
  "attester_cannot_verify",
  "blocking_flag_open",
  "no_such_record",
] as const;
export type VerifyRefusalCode = (typeof VERIFY_REFUSAL_CODES)[number];

/** The code a refusal carried, if it is one of ours. */
export function verifyRefusalCode(error: unknown): VerifyRefusalCode | null {
  if (!(error instanceof Refused)) return null;
  return (VERIFY_REFUSAL_CODES as readonly string[]).includes(error.code)
    ? (error.code as VerifyRefusalCode)
    : null;
}

export function useVerifyStatedBasis(activityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<VerifiedActivity> => {
      const { data, error, response } = await api.POST(
        "/api/v1/inventory/activities/{activity_id}/verify",
        { params: { path: { activity_id: activityId } } },
      );
      if (error !== undefined || !data) {
        throw refusalFrom(response, error, "That figure could not be accepted.");
      }
      return data;
    },
    onSettled: async (_data, error) => {
      // **Whatever the answer, this screen's copy of the record may be the thing that is wrong**:
      // `already_verified` means somebody got there first, `superseded` that a correction landed,
      // `blocking_flag_open` that the flags moved. Re-read the record and its work so the page
      // catches up with the answer it was just given.
      await queryClient.invalidateQueries({ queryKey: ["activity", activityId] });
      await queryClient.invalidateQueries({ queryKey: ["work", "record", activityId] });
      if (!error) {
        // A success changes who the receipt, the register and the reports name as the verifier.
        await queryClient.invalidateQueries();
      }
    },
  });
}
